import React, { memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { TimePicker as MaterialUiTimePicker } from '@mui/lab';
import { ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material';

import { TextField } from '~/UIKit';
import { PLAIN_GREY } from '~/Constants/Colors.constants';
import { HookClassesOverride } from '~/Helpers/TypeScript.helpers';
import { makeStyles } from '~/UIKit/Style';

interface IKeyboardTimePickerProps {
  icon?: React.ReactElement;
  onDateChange?: (name: string, value: string | null | undefined) => void;
  setFieldValue?: (name: string, value: string | null | undefined) => void;
  defaultValue?: string;
  minTime?: Date;
  value: string | null;
  error?: boolean;
  errors?: Record<string, string>;
  name?: string;
  inputFormat?: string;
  label?: string;
  disabled?: boolean;
  inputClasses?: HookClassesOverride<typeof makeStyles>;
  className?: string;
}

export const KeyboardTimePicker = memo(
  ({
    icon,
    defaultValue,
    minTime,
    value,
    error,
    errors,
    name = '',
    inputFormat,
    label,
    disabled,
    inputClasses,
    className,
    setFieldValue,
    onDateChange = setFieldValue,
  }: IKeyboardTimePickerProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const actualTime = useMemo(
      () => (value === null ? null : new Date(value || defaultValue || new Date())),
      [value, defaultValue],
    );
    const initialDate = useMemo(() => actualTime, []);

    const onUpdateDate = useCallback(
      (date: Date | null) => {
        const dateISOString = date?.toJSON() && date.toISOString();

        if (!date || !dateISOString) {
          setFieldValue?.(name, dateISOString);
          return;
        }
        const newDate = new Date(initialDate ?? date);
        newDate.setHours(date.getHours(), date.getMinutes());
        onDateChange?.(name, newDate.toISOString());
      },
      [onDateChange, name, actualTime],
    );

    return (
      <MaterialUiTimePicker
        disabled={disabled}
        inputFormat={inputFormat}
        onChange={onUpdateDate}
        value={actualTime}
        ampm={false}
        cancelText={t('app.cancel')}
        okText={t('app.confirm')}
        minTime={minTime}
        desktopModeMediaQuery={theme.breakpoints.up('md')}
        components={{
          // eslint-disable-next-line react/no-multi-comp
          OpenPickerIcon: () => (
            <ChevronRight style={{ transform: 'rotateZ(90deg)', color: PLAIN_GREY }} />
          ),
        }}
        renderInput={(textProps: any) => (
          <TextField
            {...textProps}
            newDesign
            error={!!errors}
            type="time"
            icon={icon}
            label={label}
            errorOutline={error}
            disabled={disabled}
            className={cn(inputClasses, className)}
            data-testid={`keyboardTimePicker.${name}`}
          />
        )}
      />
    );
  },
);
